import { Button, Modal, Table } from "antd";
import queryKeys from "common/queryKey";
import { useFetch } from "hooks/useFetch";
import { useQueryState } from "hooks/useQueryState";
import { getClientDetailData, useClientDetailSource } from "hooks/useClientsListSource";
import moment from "moment";
import React from "react";
import { useSelector } from "react-redux";
import { useReactToPrint } from "react-to-print";
import { getUserList, getBillingEntityList, getClientid } from "redux/selectors";
import styled from "styled-components";
import bdoLogo from "assets/bdologo01.png";
import { CALL_API } from "common/API";
import { MAIN_CLIENT_ID } from "common/Constants";

// Relative to the base URL configured inside CALL_API
// (full URL: https://h8gw6wo6o3.execute-api.us-west-1.amazonaws.com/Prod/settings/finance/finance-settings)
const FINANCE_SETTINGS_ENDPOINT = "settings/finance/finance-settings";

const CURRENCY_SYMBOLS = {
  USD: "$",
  "US Dollar": "$",
  EUR: "€",
  Euro: "€",
  GBP: "£",
  "British Pound": "£",
  INR: "₹",
};

// FIX: make sure the logo is fully loaded + decoded before printing,
// otherwise the print iframe renders a low-res / blurry version of it.
const preloadImage = (src) =>
  new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      if (img.decode) {
        img.decode().then(resolve).catch(resolve);
      } else {
        resolve();
      }
    };
    img.onerror = resolve;
    img.src = src;
  });

// Returns the first non-empty value among the candidate keys of an object
const pick = (obj, keys) => {
  if (!obj) return undefined;
  for (const k of keys) {
    const v = obj[k];
    if (v === undefined || v === null) continue;
    if (Array.isArray(v) ? v.length > 0 : String(v).trim() !== "") return v;
  }
  return undefined;
};

// ---------------------------------------------------------------------------
// Main client / client check (shared with AddNewBilling and MyProfile)
// ---------------------------------------------------------------------------
// Pass the LOGGED-IN organisation's client id (redux getClientid), exactly like
// MyProfile does. The engagement's customer id is a different thing.
// Comparison is type-safe (number vs string) and ignores case / whitespace.
// true when a client id exists and it is NOT the main client id.
export const isSubClientBilling = (clientId) => {
  const current = String(clientId ?? "").trim().toLowerCase();
  const main = String(MAIN_CLIENT_ID ?? "").trim().toLowerCase();
  return !!current && current !== main;
};

// ---------------------------------------------------------------------------
// Billing totals (shared with the billing list's Amount column)
// ---------------------------------------------------------------------------
// Total = Fee + Disbursement + Subcontract + VAT. VAT applies to the fee when
// the bill is VAT, and to each claim marked vat=true. Special offices (ids 21,
// 22) carry an additional 5% tax on VAT + Disbursement + Subcontract.
export const calculateBillingTotals = ({ fee, isVat, claims, billingOffice }) => {
  const claimList = claims ?? [];
  const feeAmount = Number(fee) || 0;

  const disbursement = claimList.reduce(
    (acc, claim) => (claim.type !== "Subcontract" ? acc + (parseInt(claim?.amount) || 0) : acc),
    0
  );
  const subcontract = claimList.reduce(
    (acc, claim) => (claim.type === "Subcontract" ? acc + (parseInt(claim?.amount) || 0) : acc),
    0
  );

  const taxPercentage = Number(billingOffice?.tax) || 0.16;
  const vatEligibleClaimsAmount = claimList.reduce(
    (acc, claim) => (claim.vat === true ? acc + (parseInt(claim?.amount) || 0) : acc),
    0
  );

  // VAT applied only where VAT is set - never double-applied
  const vatFromBilling = isVat ? feeAmount * taxPercentage : 0;
  const vatFromClaims = vatEligibleClaimsAmount * taxPercentage;
  const vatAmount = Number((vatFromBilling + vatFromClaims).toFixed(2));

  const subtotal = feeAmount + disbursement + subcontract;

  const isSpecialOffice = billingOffice?.id === 22 || billingOffice?.id === 21;
  const specialAdditionalTax = isSpecialOffice
    ? Number(((vatAmount + disbursement + subcontract) * 0.05).toFixed(2))
    : 0;

  return {
    disbursement,
    subcontract,
    taxPercentage,
    vatAmount,
    subtotal,
    isSpecialOffice,
    specialAdditionalTax,
    totalTax: vatAmount + specialAdditionalTax,
    grandTotal: subtotal + vatAmount + specialAdditionalTax,
  };
};

// Billing office of a bill: the engagement's billing office id (same as the
// invoice uses), otherwise the office name saved on the bill
export const findBillingOffice = (billingOfficeList, officeName, officeId) => {
  const matched =
    billingOfficeList?.find((item) => item.id == officeId) ||
    (officeName && billingOfficeList?.find((item) => item.name === officeName));
  return matched ? { ...matched, tax: Number(matched.tax) || 0 } : { id: null, name: "", tax: 0 };
};

// Telephone can arrive as a string or an array of strings -> "a / b / c"
const normalizePhones = (value) => {
  if (!value) return undefined;
  if (Array.isArray(value)) {
    const list = value.map((p) => String(p).trim()).filter(Boolean);
    return list.length ? list.join(" / ") : undefined;
  }
  return String(value);
};

// "987654" -> "P O Box 987654" (leaves values that already contain "box" untouched)
const formatPoBox = (value) => {
  if (!value) return undefined;
  const str = String(value).trim();
  if (!str) return undefined;
  return /box/i.test(str) ? str : `P O Box ${str}`;
};

// Bank accounts can arrive as a string, an array of strings, or an array of {currency, accountNumber}
const normalizeBankAccounts = (value) => {
  if (!value) return undefined;
  if (typeof value === "string") return value;
  if (Array.isArray(value)) {
    return value
      .map((item) => {
        if (typeof item === "string") return item;
        const cur = pick(item, ["currency", "cur", "type"]);
        const acc = pick(item, ["accountNumber", "account", "accountNo", "number", "value"]);
        return acc ? `${cur ? `${cur} a/c ` : ""}${acc}` : null;
      })
      .filter(Boolean)
      .join("\n");
  }
  return undefined;
};

// Candidate keys that may hold the client's PO Box / postal code
const PO_BOX_KEYS = [
  "bdo_postalcode",
  "address1_postofficebox",
  "bdo_pobox",
  "bdo_postofficebox",
  "poBox",
  "po_box",
  "pobox",
  "postBox",
  "postalCode",
  "address1_postalcode",
];

// ---------------------------------------------------------------------------
// Legacy design table columns
// ---------------------------------------------------------------------------
const columns = [
  {
    title: "S.No",
    dataIndex: "sno",
    key: "sno",
    render: (_, record) => <>{record.sno}</>,
    width: "5%",
  },
  {
    title: "Column 1",
    dataIndex: "column1",
    key: "column1",
    onCell: (record) => ({ colSpan: record.column1.colSpan }),
    render: (text, record) => record.column1.value,
    width: "25%",
  },
  {
    title: "Column 2",
    dataIndex: "column2",
    key: "column2",
    onCell: (record) => ({ colSpan: record.column2.colSpan }),
    render: (text, record) => record.column2.value,
    width: "35%",
  },
  {
    title: "Column 3",
    dataIndex: "column3",
    key: "column3",
    onCell: (record) => ({ colSpan: record.column3.colSpan }),
    render: (text, record) => record.column3.value,
    className: "fw-bold",
    width: "15%",
  },
  {
    title: "Column 4",
    dataIndex: "column4",
    key: "column4",
    onCell: (record) => ({ colSpan: record.column4.colSpan }),
    render: (text, record) => record.column4.value,
    align: "right",
    width: "20%",
  },
];

const BillingInvoice = ({ billingInvoiceData, open, handleClose, engagementDataForDirectPreview, formValues }) => {
  const userList = useSelector(getUserList);
  const billingOfficeList = useSelector(getBillingEntityList);
  // Logged-in organisation's client id (same source MyProfile uses)
  const ClientId = useSelector(getClientid);
  const getClientDetailSource = useClientDetailSource();

  const clientCode = billingInvoiceData?.clientcode;
  const engagementId = billingInvoiceData?.engagementid;

  const { key, url } = getClientDetailSource(clientCode);
  const { data: clientDataRes } = useFetch(key, url, { endpoint: !!clientCode });

  const { key: engagementKey, url: engagementUrl } = queryKeys.getEngagementById(engagementId);

  const { isFetched } = useQueryState(key);

  const { data: engagementDataRes } = useFetch(engagementKey, engagementUrl, {
    enabled: !isFetched && !!engagementId,
  });

  const engagementData = engagementDataRes?.data ? engagementDataRes?.data : engagementDataForDirectPreview;

  const clientData = getClientDetailData(clientDataRes);

  // ---------------------------------------------------------------------------
  // Which design to show?
  //   ClientId !== MAIN_CLIENT_ID -> NEW proforma design (with due date)
  //   otherwise                   -> OLD design
  // ---------------------------------------------------------------------------
  const isNewDesign = isSubClientBilling(ClientId);

  // ---------------------------------------------------------------------------
  // Finance settings API (only needed by the new design)
  // ---------------------------------------------------------------------------
  const [financeSettings, setFinanceSettings] = React.useState(null);

  // The billing entity this engagement bills under - finance settings are
  // scoped to it, so each billing entity shows its own company/bank info.
  const billingOfficeId = engagementData?.financial_resourcePlan?.billingOffice;

  React.useEffect(() => {
    if (!open || !isNewDesign) return undefined;
    let cancelled = false;

    const loadFinanceSettings = async () => {
      try {
        const url = billingOfficeId
          ? `${FINANCE_SETTINGS_ENDPOINT}?entityId=${billingOfficeId}`
          : FINANCE_SETTINGS_ENDPOINT;
        const res = await CALL_API(url, "get");
        if (!cancelled) setFinanceSettings(res);
      } catch (err) {
        console.error("finance-settings API error:", err);
      }
    };

    loadFinanceSettings();
    return () => {
      cancelled = true;
    };
  }, [open, isNewDesign, billingOfficeId]);

  const printComponentRef = React.useRef(null);

  // FIX: wait for the logo to be loaded/decoded, and force exact colour/image
  // rendering on the printed page.
  const handlePrint = useReactToPrint({
    content: () => printComponentRef.current,
    bodyClass: "p-3",
    onBeforeGetContent: () => preloadImage(bdoLogo),
    pageStyle: `
      @page { size: A4; margin: 12mm; }
      @media print {
        html, body {
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
        img {
          image-rendering: auto !important;
          -webkit-print-color-adjust: exact !important;
          print-color-adjust: exact !important;
        }
      }
    `,
  });

  // ---------------------------------------------------------------------------
  // Billing office
  // ---------------------------------------------------------------------------
  const matchedBillingOffice = billingOfficeList.find(
    (item) => item.id == engagementData?.financial_resourcePlan?.billingOffice
  );
  const billingOfficeData = matchedBillingOffice
    ? { ...matchedBillingOffice, tax: Number(matchedBillingOffice.tax) || 0 }
    : { id: null, name: "", tax: 0 };

  // Find the settings entry for this billing office (or the only entry / plain object)
  const settingsRoot = financeSettings?.data ?? financeSettings?.settings ?? financeSettings;
  const settingsList = Array.isArray(settingsRoot) ? settingsRoot : settingsRoot ? [settingsRoot] : [];
  const matchedSettings =
    settingsList.find(
      (s) =>
        String(
          pick(s, ["financeSettingId", "id", "billingOfficeId", "billingOffice", "officeId", "entityId"])
        ) === String(billingOfficeData.id) ||
        (billingOfficeData.name &&
          String(
            pick(s, ["companyName", "name", "officeName", "billingOfficeName", "entityName"]) || ""
          ).toLowerCase() === billingOfficeData.name.toLowerCase())
    ) || (settingsList.length === 1 ? settingsList[0] : null);

  // Patch billing office fields with API values when the API provides them
  const office = {
    ...billingOfficeData,
    // name is intentionally NOT patched from the API; keep the billing office name as is
    name: billingOfficeData.name,
    address: pick(matchedSettings, ["address", "officeAddress", "registeredAddress"]) ?? billingOfficeData.address,
    poBox: formatPoBox(pick(matchedSettings, ["poBox", "po_box", "pobox", "postBox"])) ?? billingOfficeData.poBox,
    city: pick(matchedSettings, ["city", "location"]) ?? billingOfficeData.city,
    phone:
      normalizePhones(pick(matchedSettings, ["telephone", "phone", "tel", "phoneNumber", "contactNumber"])) ??
      billingOfficeData.phone,
    email: pick(matchedSettings, ["email", "emailAddress", "financeEmail"]) ?? billingOfficeData.email,
    tpin: pick(matchedSettings, ["tpin", "TPIN", "pin", "taxPin", "taxId"]) ?? billingOfficeData.tpin,
    bankName: pick(matchedSettings, ["bankName", "bank"]) ?? billingOfficeData.bankName,
    bankBranch: pick(matchedSettings, ["bankBranch", "branch", "branchName"]) ?? billingOfficeData.bankBranch,
    bankAccounts:
      normalizeBankAccounts(pick(matchedSettings, ["bankAccounts", "bankAccount", "accounts", "accountDetails"])) ??
      billingOfficeData.bankAccounts,
    swiftCode: pick(matchedSettings, ["swiftCode", "swift", "swift_code", "swiftcode"]) ?? billingOfficeData.swiftCode,
  };

  const billingOfficeName = office?.name;

  // ---------------------------------------------------------------------------
  // Shared invoice data
  // ---------------------------------------------------------------------------
  const competency = engagementData?.client?.competency;
  const serviceLine = engagementData?.client?.serviceLine;

  const clientName = engagementData?.client?.clientName || clientData?.registeredName;
  const address = clientData?.bdo_registeredaddress ? clientData?.bdo_registeredaddress : billingInvoiceData?.address;
  const city = [clientData?.address1_city, clientData?.country].filter(Boolean).join(", ");
  // Client PO Box: look in client details first, then fall back to the billing record
  const poBox = formatPoBox(pick(clientData, PO_BOX_KEYS) ?? pick(billingInvoiceData, PO_BOX_KEYS));
  const registeredNumber = billingInvoiceData?.loerefno;
  const pinNumber = clientData?.bdo_pin ? clientData?.bdo_pin : billingInvoiceData?.bdo_pin;
  const clientEmailAddress = clientData?.emailaddress1 ? clientData?.emailaddress1 : billingInvoiceData?.emailaddress1;

  const contactPerson = billingInvoiceData?.contactperson
    ? billingInvoiceData?.contactperson
    : billingInvoiceData?.contactpersonid;
  const contactPersonAc = billingInvoiceData?.contactpersonac
    ? billingInvoiceData?.contactpersonac
    : billingInvoiceData?.contactpersonacid;

  const description = formValues?.description ? formValues?.description : billingInvoiceData?.description;
  const billingdate = billingInvoiceData?.billingdate;
  const dueDate = billingInvoiceData?.duedate;
  const invoiceNumber = "-";
  const reference = billingInvoiceData?.loerefno || "-";

  const currency = engagementData?.financial_resourcePlan?.currency;
  const currencySymbol = CURRENCY_SYMBOLS[currency] ?? (currency ? `${currency} ` : "");

  const cleanAmount = (value) => {
    if (!value) return 0;
    return parseInt(String(value).replace(/[$,]/g, ""), 10) || 0;
  };

  const fees = cleanAmount(billingInvoiceData?.amount);
  const amountForVAT = cleanAmount(formValues?.amount);

  const billingFeeBase = Number(formValues?.amount) || fees;
  const claims = billingInvoiceData?.claims ?? [];

  const {
    disbursement: Disbursement,
    subcontract: Subcontract,
    taxPercentage,
    vatAmount,
    subtotal,
    isSpecialOffice,
    specialAdditionalTax,
    totalTax,
    grandTotal,
  } = calculateBillingTotals({
    fee: billingFeeBase,
    // Billing is VAT only if explicitly marked VAT
    isVat: billingInvoiceData?.vat === "VAT" || formValues?.vat === "VAT",
    claims,
    billingOffice: billingOfficeData,
  });

  // ===========================================================================
  // NEW DESIGN (ClientId !== MAIN_CLIENT_ID)
  // ===========================================================================
  if (isNewDesign) {
    // Tax percentage label, e.g. "(16%)" or "(16% + 5%)" for special offices
    const formatPercent = (fraction) => `${Number((fraction * 100).toFixed(2))}%`;
    const taxPercentLabel =
      totalTax > 0
        ? `(${formatPercent(taxPercentage)}${isSpecialOffice ? ` + ${formatPercent(0.05)}` : ""})`
        : "";

    const formatMoney = (value) =>
      Number(value || 0).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 });

    const formatDate = (value) => (value ? moment(value).format("MMM DD, YYYY") : "-");

    // Disbursement / Subcontract sections are always listed (0.00 without claims)
    const lineItems = [
      { label: description || "-", amount: billingFeeBase },
      { label: "Disbursement", amount: Disbursement },
      { label: "Subcontract", amount: Subcontract },
    ];

    const bankLine = [office?.bankName, office?.bankBranch].filter(Boolean).join(", ");

    return (
      <StyledModal
        open={open}
        onCancel={handleClose}
        width={900}
        footer={null}
        closable={true}
        maskClosable={false}
        centered={true}
      >
        <InvoiceWrap ref={printComponentRef}>
          {/* Header */}
          <div className="top">
            {/* FIX: explicit height, decoding sync, eager loading */}
            <img src={bdoLogo} alt="BDO" className="logo" height={52} decoding="sync" loading="eager" />
            <h1 className="doc-title">Proforma Invoice</h1>
          </div>

          {/* Parties */}
          <div className="parties">
            <div className="bill-to">
              <div className="bold">Bill to</div>
              {clientName && <div>{clientName}</div>}
              {address && <div className="pre-line">{address}</div>}
              {poBox && <div>{poBox}</div>}
              {city && <div>{city}</div>}
              {clientEmailAddress && <div className="email">{clientEmailAddress}</div>}
            </div>

            <div className="issuer">
              {billingOfficeName && <div>{billingOfficeName}</div>}
              {office?.address && <div className="pre-line">{office.address}</div>}
              {office?.poBox && <div>{office.poBox}</div>}
              {office?.city && <div>{office.city}</div>}
              {office?.phone && <div>Tel: {office.phone}</div>}
              {office?.email && <div>Email: {office.email}</div>}
              {office?.tpin && <div>TPIN: {office.tpin}</div>}
            </div>
          </div>

          {/* Summary strip */}
          <div className="summary">
            <div>
              <div className="label">Amount due</div>
              <div className="value strong">
                {currencySymbol}
                {formatMoney(grandTotal)}
              </div>
            </div>
            <div>
              <div className="label">Due date</div>
              <div className="value">{formatDate(dueDate)}</div>
            </div>
            <div>
              <div className="label">Issue date</div>
              <div className="value">{formatDate(billingdate)}</div>
            </div>
            <div>
              <div className="label">Invoice number</div>
              <div className="value">{invoiceNumber}</div>
            </div>
            <div>
              <div className="label">Reference</div>
              <div className="value">{reference}</div>
            </div>
          </div>

          {/* Line items */}
          <table className="items">
            <thead>
              <tr>
                <th>Description</th>
                <th className="num">Amount</th>
              </tr>
            </thead>
            <tbody>
              {lineItems.map((item, index) => (
                <tr key={index}>
                  <td>{item.label}</td>
                  <td className="num">{formatMoney(item.amount)}</td>
                </tr>
              ))}
            </tbody>
          </table>

          {/* Payment details + totals */}
          <div className="footer-grid">
            <div className="payment">
              <div>Payment can be made direct to our bank – details :</div>
              {bankLine && <div>{bankLine}</div>}
              {office?.bankAccounts &&
                String(office.bankAccounts)
                  .split("\n")
                  .map((line, i) => <div key={i}>{line}</div>)}
              {office?.swiftCode && <div>Swift Code : {office.swiftCode}</div>}
            </div>

            <div className="totals">
              <div className="t-row">
                <span>Subtotal</span>
                <span>{formatMoney(subtotal)}</span>
              </div>
              <div className="t-row">
                <span>Total tax {taxPercentLabel}</span>
                <span>{formatMoney(totalTax)}</span>
              </div>
              <div className="t-row divided">
                <span>Total</span>
                <span>{formatMoney(grandTotal)}</span>
              </div>
              <div className="t-row due">
                <span>Amount due</span>
                <span className="due-amount">
                  {currencySymbol}
                  {formatMoney(grandTotal)}
                </span>
              </div>
            </div>
          </div>
        </InvoiceWrap>

        <div style={{ display: "flex", gap: "8px", justifyContent: "end" }} className="mb-3 mt-2">
          <div>
            <Button type="primary" className="ms-0" onClick={handlePrint}>
              Print Invoice
            </Button>
          </div>
        </div>
      </StyledModal>
    );
  }

  // ===========================================================================
  // OLD DESIGN (ClientId === MAIN_CLIENT_ID, or no client id)
  // ===========================================================================
  const heading = fees ? fees : amountForVAT > 0 ? "REQUEST FOR FEE NOTE" : "REQUEST FOR CREDIT NOTE";

  const partnerName = userList?.find((user) => user.userid === engagementData?.stakeHolder?.partner)?.fullname;

  const personInCharge = userList?.find((user) => user.userid === engagementData?.stakeHolder?.personIncharge)?.fullname;

  const billingStatus = billingInvoiceData?.statusname;
  const approvedby = billingInvoiceData?.engagementPartner;
  const approvedbyName = userList?.find((user) => user?.userid === approvedby)?.fullname;

  const formatAmountINR = (amount) => {
    const cleaned = String(amount).replace(/[^0-9.-]/g, ""); // Remove $, commas etc.
    const parsed = Number(cleaned.split(".")[0]); // Take only the integer part
    if (isNaN(parsed)) return "0";
    return new Intl.NumberFormat("en-US").format(parsed);
  };

  const dataSource = [
    {
      sno: 1,
      column1: { value: "Department", colSpan: 1 },
      column2: { value: competency, colSpan: 3 },
      column3: { value: "", colSpan: 0 },
      column4: { value: "", colSpan: 0 },
    },
    {
      sno: 2,
      column1: { value: "Company", colSpan: 1 },
      column2: { value: billingOfficeName, colSpan: 1 },
      column3: { value: "Date", colSpan: 1 },
      column4: { value: billingdate ? moment(billingdate).format("DD MMM YYYY") : "-", colSpan: 1 },
    },
    {
      sno: 3,
      column1: { value: "Service Line", colSpan: 1 },
      column2: { value: serviceLine, colSpan: 3 },
      column3: { value: "", colSpan: 0 },
      column4: { value: "", colSpan: 0 },
    },
    {
      sno: 4,
      column1: { value: "Client Name", colSpan: 1 },
      column2: { value: clientName, colSpan: 3 },
      column3: { value: "", colSpan: 0 },
      column4: { value: "", colSpan: 0 },
    },
    {
      sno: 5,
      column1: { value: "Address", colSpan: 1 },
      column2: { value: address, colSpan: 3 },
      column3: { value: "", colSpan: 0 },
      column4: { value: "", colSpan: 0 },
    },
    {
      sno: 6,
      column1: { value: "Contact Person", colSpan: 1 },
      column2: { value: contactPerson, colSpan: 3 },
      column3: { value: "", colSpan: 0 },
      column4: { value: "", colSpan: 0 },
    },
    {
      sno: "",
      column1: { value: "Contact Person A/C", colSpan: 1 },
      column2: { value: contactPersonAc, colSpan: 3 },
      column3: { value: "", colSpan: 0 },
      column4: { value: "", colSpan: 0 },
    },
    {
      sno: "",
      column1: { value: "Email addresses", colSpan: 1 },
      column2: { value: clientEmailAddress, colSpan: 3 },
      column3: { value: "", colSpan: 0 },
      column4: { value: "", colSpan: 0 },
    },
    {
      sno: "",
      column1: { value: "Engagement ID", colSpan: 1 },
      column2: { value: engagementData?.engagementId ?? "-", colSpan: 3 },
      column3: { value: "", colSpan: 0 },
      column4: { value: "", colSpan: 0 },
    },
    {
      sno: "",
      column1: { value: "Billing ID", colSpan: 1 },
      column2: { value: billingInvoiceData?.billingid ?? "-", colSpan: 3 },
      column3: { value: "", colSpan: 0 },
      column4: { value: "", colSpan: 0 },
    },
    {
      sno: 7,
      column1: { value: "LOE Ref No", colSpan: 1 },
      column2: { value: registeredNumber, colSpan: 1 },
      column3: { value: "Client PIN", colSpan: 1 },
      column4: { value: pinNumber, colSpan: 1 },
    },
    {
      sn: 8,
      column1: { value: "Code (Time sheet system)", colSpan: 1 },
      column2: { value: "", colSpan: 1 },
      column3: { value: "Currency", colSpan: 1 },
      column4: {
        value: engagementData?.financial_resourcePlan?.currency,
        colSpan: 1,
      },
    },
    {
      sno: 9,
      column1: { value: "Description of Services Rendered", colSpan: 1 },
      column2: { value: description, colSpan: 3 },
      column3: { value: "", colSpan: 0 },
      column4: { value: "", colSpan: 0 },
    },
    {
      sno: 10,
      column1: { value: "Fees", colSpan: 1 },
      column2: { value: "", colSpan: 1 },
      column3: { value: "", colSpan: 1 },
      column4: { value: formatAmountINR(formValues?.amount || fees), colSpan: 1 },
    },
    {
      sno: "",
      column1: { value: "Disbursement(Claims)", colSpan: 1 },
      column2: { value: "", colSpan: 1 },
      column3: { value: "", colSpan: 1 },
      column4: { value: formatAmountINR(Disbursement), colSpan: 1 },
    },
    {
      sno: "",
      column1: { value: "Subcontract(Claims)", colSpan: 1 },
      column2: { value: "", colSpan: 1 },
      column3: { value: "", colSpan: 1 },
      column4: { value: formatAmountINR(Subcontract), colSpan: 1 },
    },
    {
      sno: "",
      column1: { value: `VAT - ${taxPercentage * 100}%`, colSpan: 1 },
      column2: { value: "", colSpan: 1 },
      column3: { value: "", colSpan: 1 },
      column4: {
        value: formatAmountINR(vatAmount.toFixed(2)),
        colSpan: 1,
      },
    },

    ...(isSpecialOffice
      ? [
        {
          sno: "",
          column1: { value: "Additional tax on VAT", colSpan: 1 },
          column2: { value: "", colSpan: 1 },
          column3: { value: "", colSpan: 1 },
          column4: {
            value: Number(specialAdditionalTax || 0).toFixed(2),
            colSpan: 1,
          },
        },
      ]
      : []),

    {
      sno: 11,
      column1: { value: "TOTAL", colSpan: 1 },
      column2: { value: "", colSpan: 1 },
      column3: { value: "", colSpan: 1 },
      column4: {
        value:
          Number(grandTotal).toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) || "-",
        colSpan: 1,
      },
    },
    {
      sno: 12,
      column1: { value: "Name of Incharge", colSpan: 1 },
      column2: { value: "", colSpan: 1 },
      column3: { value: "", colSpan: 1 },
      column4: { value: "", colSpan: 1 },
    },
    {
      sno: "",
      column1: { value: "Partner", colSpan: 1 },
      column2: { value: partnerName, colSpan: 1 },
      column3: { value: "Senior", colSpan: 1 },
      column4: { value: "", colSpan: 1 },
    },
    {
      sno: "",
      column1: { value: "Manager", colSpan: 1 },
      column2: { value: personInCharge, colSpan: 1 },
      column3: { value: "Semi Senior", colSpan: 1 },
      column4: { value: "", colSpan: 1 },
    },
    {
      sno: 13,
      column1: {
        value: "Staff responsible for cash collection for this job and debtor's ledger reconciliation",
        colSpan: 2,
      },
      column2: { value: "", colSpan: 0 },
      column3: { value: "", colSpan: 1 },
      column4: { value: "", colSpan: 1 },
    },
  ];

  return (
    <LegacyModal
      open={open}
      onCancel={handleClose}
      title={billingOfficeName}
      width={1200}
      footer={null}
      closable={true}
      maskClosable={false}
      centered={true}
    >
      <BillingInvoiceWrap ref={printComponentRef}>
        <div className="invoice-heading">INVOICE/CREDIT NOTE Raising Request</div>
        <div>
          <div className="heading">{heading}</div>

          <BillingInvoiceTable
            columns={columns}
            dataSource={dataSource}
            bordered={true}
            pagination={false}
            showHeader={false}
          />
          <div className="section-row">
            <div className="left">{billingStatus ? billingStatus : "Pending"}</div>
            <div className="right">
              {billingStatus === "Approved by Partner" || billingStatus === "Approved by Finance"
                ? approvedbyName
                : "-"}
            </div>
          </div>

          <div className="section-row">
            <div className="left">Signature</div>
            <div className="right">{billingdate ? moment(billingdate).format("DD MMM YYYY") : "-"}</div>
          </div>

          <div className="accepatance">
            I, certify that the above amount billed is in accordance with the engagement letter and/or the assignment
            has been completed at client satisfaction.
          </div>
        </div>
      </BillingInvoiceWrap>
      <div style={{ display: "flex", gap: "8px", justifyContent: "end" }} className="mb-3 mt-2">
        <div>
          <Button type="primary" className="ms-0" onClick={handlePrint}>
            Print Invoice
          </Button>
        </div>
      </div>
    </LegacyModal>
  );
};

export default BillingInvoice;

// ===========================================================================
// NEW DESIGN styles
// ===========================================================================
const InvoiceWrap = styled.div`
  padding: 28px 32px;
  color: #111;
  font-size: 12px;
  line-height: 1.55;

  .bold {
    font-weight: 700;
  }
  .pre-line {
    white-space: pre-line;
  }

  .top {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
  }

  /* FIX: crisp logo - no transforms/filters, fixed height, exact print rendering */
  .logo {
    height: 52px;
    width: auto;
    display: block;
    object-fit: contain;
    image-rendering: auto;
    transform: none;
    filter: none;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }
  .doc-title {
    font-size: 22px;
    font-weight: 700;
    margin: 0;
  }

  .parties {
    display: flex;
    justify-content: space-between;
    gap: 40px;
    margin-top: 28px;
  }
  .bill-to {
    max-width: 240px;
  }
  .bill-to .email {
    margin-top: 12px;
  }
  .issuer {
    text-align: right;
    max-width: 280px;
  }

  .summary {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 24px;
    margin-top: 28px;
    padding-bottom: 20px;
    border-bottom: 2px solid #e4002b;
  }
  .summary > div {
    display: flex;
    flex-direction: column;
    gap: 4px;
    width: auto;
    max-width: none;
  }
  .summary .label {
    font-size: 11px;
    color: #333;
  }
  .summary .value {
    font-size: 13px;
    font-weight: 700;
    line-height: 1.2;
    white-space: nowrap;
  }
  .summary .value.strong {
    font-size: 16px;
  }

  .items {
    width: 100%;
    margin-top: 12px;
    table-layout: fixed;
    border-collapse: collapse;
  }
  .items th {
    font-weight: 400;
    text-align: left;
    padding: 6px 0;
    border-bottom: 1px solid #bbb;
  }
  .items td {
    padding: 8px 0;
    border-bottom: 1px solid #e5e5e5;
  }
  .items .num {
    text-align: right;
    width: 110px;
    white-space: nowrap;
  }

  .footer-grid {
    display: flex;
    justify-content: space-between;
    align-items: flex-start;
    gap: 40px;
    margin-top: 20px;
  }
  .payment {
    max-width: 320px;
  }
  .totals {
    width: 280px;
  }
  .totals .t-row {
    display: flex;
    flex-direction: row;
    flex-wrap: nowrap;
    justify-content: space-between;
    align-items: baseline;
    width: 100%;
    padding: 6px 0;
  }
  .totals .t-row > span {
    width: auto;
    max-width: none;
  }
  .totals .t-row.divided {
    margin-top: 6px;
    border-top: 1px solid #bbb;
    padding-top: 10px;
  }
  .totals .t-row.due {
    margin-top: 6px;
    border-top: 1px solid #111;
    padding-top: 10px;
    font-weight: 700;
    align-items: baseline;
  }
  .totals .due-amount {
    font-size: 16px;
  }

  @media print {
    padding: 0;

    .logo {
      height: 52px;
      width: auto;
      image-rendering: auto;
      -webkit-print-color-adjust: exact;
      print-color-adjust: exact;
    }
  }
`;

const StyledModal = styled(Modal)`
  .ant-modal-close-x {
    .anticon-close.ant-modal-close-icon,
    svg {
      width: 20px;
      height: 20px;
    }
  }
  .ant-modal-header {
    display: none;
  }
  .ant-modal-close {
    top: 16px;
    right: 16px;
  }
  .ant-modal-title {
    font-weight: 500;
    text-align: left;
    color: #1b1b1b;
  }
  .ant-modal-content {
    overflow: hidden;
  }
  .anticon {
    color: #000000;
    width: 14px;
    height: 14px;
  }
  .ant-modal-body {
    padding-top: 16px;
    height: 85vh;
    overflow-y: scroll;
  }
  .ant-modal-footer {
    display: none;
  }
`;

// ===========================================================================
// OLD DESIGN styles
// ===========================================================================
const BillingInvoiceWrap = styled.div`
  .invoice-heading {
    font-size: 17px;
    font-weight: 600;
    margin-bottom: 10px;
    text-align: start;
  }
  .heading {
    font-weight: 600;
    margin-bottom: 10px;
    text-align: center;
  }
  .section-row {
    display: flex;
    justify-content: space-between;
    align-items: center;
    margin-top: 10px;
    font-weight: 600;
    .right {
      padding: 10px 40px 10px 40px;
      border-bottom: 2px solid #000;
      min-width: 160px;
    }
  }
  .accepatance {
    font-weight: 600;
    margin-top: 20px;
  }
`;

const BillingInvoiceTable = styled(Table)`
  .ant-table-cell.nowrap {
    white-space: nowrap;
  }
  .ant-table-cell.border-right-none {
    border-right: none !important;
  }
  .ant-table-cell.border-bottom-none {
    border-bottom: none;
  }
  .ant-table-cell {
    padding: 6px 10px !important;
    font-size: 14px;
    font-weight: 500;
  }
  .ant-table-thead > tr > th {
    overflow-wrap: normal;
  }
  .ant-table-cell:has(.extra-section) {
    border-bottom: 5px solid #f0f0f0;
  }
  margin-bottom: 20px;
`;

const LegacyModal = styled(Modal)`
  .ant-modal-close-x {
    .anticon-close.ant-modal-close-icon,
    svg {
      width: 20px;
      height: 20px;
    }
  }
  .ant-modal-header {
    border-bottom: none;
    padding-top: 10px;
    padding-bottom: 10px !important;
  }
  .ant-modal-title {
    font-weight: 500;
    text-align: left;
    color: #1b1b1b;
  }
  .ant-modal-content {
    border-radius: 10px;
  }
  .anticon {
    color: #000000;
    width: 14px;
    height: 14px;
  }
  .ant-modal-body {
    padding-top: 0px;
    height: 85vh;
    overflow-y: scroll;
  }
  .ant-modal-footer {
    display: none;
  }
  .ant-btn-text:focus,
  .ant-btn-text:hover {
    background: #26a4ff;
    color: #ffffff;
  }
  .ant-checkbox.ant-checkbox-checked {
    .ant-checkbox-inner {
      background-color: #02b209 !important;
      border-color: #02b209 !important;
    }
  }
  .ant-upload-span {
    cursor: pointer;
  }
`;